import React, { useState, useEffect, useCallback } from 'react';
import './MyRegistrations.css';
import Sidebar from '../../components/Sidebar';
import SvgIcon from '../../components/SvgIcon';

const MyRegistrations = ({ user, onLogout, onSwitchView }) => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  // Modal State for Uploading Bank Slip
  const [uploadTargetReg, setUploadTargetReg] = useState(null);
  const [bankSlipFile, setBankSlipFile] = useState(null);
  const [bankSlipPreview, setBankSlipPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Modal State for Viewing Bank Slip
  const [viewProofModal, setViewProofModal] = useState(null);

  // Withdraw State
  const [withdrawingRegId, setWithdrawingRegId] = useState(null);

  // Attendance Self Check-In State
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [sessionCodeInput, setSessionCodeInput] = useState('');
  const [submittingCode, setSubmittingCode] = useState(false);
  const [attendanceMsg, setAttendanceMsg] = useState(null);

  // Learner Certificates State
  const [myCertificates, setMyCertificates] = useState([]);

  // Blackout Suspension State
  const [learnerBlackout, setLearnerBlackout] = useState(null);

  // Lock body scroll when any modal is open
  const isAnyModalOpen = Boolean(uploadTargetReg || viewProofModal || withdrawingRegId || attendanceModalOpen);
  useEffect(() => {
    if (isAnyModalOpen) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }
  }, [isAnyModalOpen]);

  const fetchMyRegistrations = useCallback(async (isSilent = false) => {
    if (!user?.email) return;
    if (!isSilent) setLoading(true);
    try {
      const res = await fetch(`/api/learner/registrations/${encodeURIComponent(user.email)}`);
      const data = await res.json();
      if (res.status === 403 && (data.status === 'suspended' || data.status === 'blackout')) {
        alert(data.message || 'Your account has been suspended for 2 months due to attendance infractions. You will now be signed out.');
        if (onLogout) onLogout();
        return;
      }
      if (res.ok && data.status === 'success') {
        setRegistrations(data.data);
      } else if (!isSilent) {
        setToast({ type: 'error', msg: data.message || 'Failed to load your registrations.' });
      }

      // Also fetch certificates
      const certRes = await fetch(`/api/learner/certificates/${encodeURIComponent(user.email)}`);
      const certData = await certRes.json();
      if (certRes.ok && certData.status === 'success') {
        setMyCertificates(certData.data || []);
      }

      // Check blackout status
      const boRes = await fetch(`/api/learner/blackout-status/${encodeURIComponent(user.email)}`);
      const boData = await boRes.json();
      if (boRes.ok && boData.status === 'success') {
        setLearnerBlackout(boData.data);
      }
    } catch (err) {
      console.error(err);
      if (!isSilent) setToast({ type: 'error', msg: 'Failed to connect to server.' });
    } finally {
      if (!isSilent) setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    if (user?.email) {
      fetchMyRegistrations();
      // Set auto-polling every 4 seconds to catch live status updates from Admin
      const interval = setInterval(() => {
        fetchMyRegistrations(true);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [user, fetchMyRegistrations]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setToast({ type: 'error', msg: 'File size exceeds 5MB limit. Please upload a smaller image or document.' });
      return;
    }

    setBankSlipFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setBankSlipPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadTargetReg || (!bankSlipFile && !bankSlipPreview)) {
      setToast({ type: 'error', msg: 'Please select a valid image or document file.' });
      return;
    }

    setUploading(true);
    try {
      let res;
      if (bankSlipFile) {
        const formData = new FormData();
        formData.append('file', bankSlipFile);
        res = await fetch(`/api/learner/registrations/${uploadTargetReg.RegistrationID}/upload-bank-slip`, {
          method: 'POST',
          body: formData
        });
      } else {
        res = await fetch(`/api/learner/registrations/${uploadTargetReg.RegistrationID}/upload-bank-slip`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bankSlipFile: bankSlipPreview })
        });
      }

      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: 'Bank slip uploaded successfully! Your payment proof is now pending admin verification.' });
        setUploadTargetReg(null);
        setBankSlipFile(null);
        setBankSlipPreview(null);
        fetchMyRegistrations();
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to upload bank slip.' });
      }
    } catch (err) {
      console.error(err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    } finally {
      setUploading(false);
    }
  };

  const handleExecuteWithdraw = async (registrationId) => {
    try {
      const res = await fetch(`/api/learner/registrations/${registrationId}/withdraw`, {
        method: 'PUT'
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: 'Successfully withdrawn from training registration.' });
        setWithdrawingRegId(null);
        fetchMyRegistrations();
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to withdraw.' });
      }
    } catch (err) {
      console.error(err);
      setToast({ type: 'error', msg: 'Connection error. Failed to withdraw.' });
    }
  };

  const handleAttendanceSubmit = async (e) => {
    e.preventDefault();
    if (!sessionCodeInput.trim()) return;
    setSubmittingCode(true);
    setAttendanceMsg(null);
    try {
      const res = await fetch('/api/learner/attendance/self-checkin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          learnerEmail: user?.email,
          sessionCode: sessionCodeInput.trim()
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setAttendanceMsg({ type: 'success', text: data.message });
        setSessionCodeInput('');
        setToast({ type: 'success', msg: data.message });
        fetchMyRegistrations(false);
      } else {
        setAttendanceMsg({ type: 'error', text: data.message || 'Check-in failed.' });
      }
    } catch (err) {
      console.error(err);
      setAttendanceMsg({ type: 'error', text: 'Connection error. Failed to process attendance check-in.' });
    } finally {
      setSubmittingCode(false);
    }
  };

  const formatToDMY = (dateStr) => {
    if (!dateStr) return 'TBD';
    const parts = dateStr.split(' ')[0].split('-');
    if (parts.length < 3) return dateStr;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const parseSegments = (raw) => {
    if (!raw) return null;
    if (Array.isArray(raw)) return raw;
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
    } catch (e) {
      return null;
    }
  };

  const filteredRegistrations = registrations.filter(r => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'pending') return r.Status === 'Pending Approval';
    if (activeFilter === 'payment') return r.Status === 'Pending Payment' || r.Status === 'Payment Submitted';
    if (activeFilter === 'approved') return r.Status === 'Approved';
    return true;
  });

  return (
    <div className="profile-page-layout">
      {/* Floating Toast Notification */}
      {toast && (
        <div className={`profile-toast ${toast.type}`}>
          <div className="profile-toast-icon">
            <SvgIcon name={toast.type === 'success' ? 'checkCircle' : 'alertCircle'} size={20} />
          </div>
          <div className="profile-toast-content">
            <span className="profile-toast-title">{toast.type === 'success' ? 'Success!' : 'Error'}</span>
            <span className="profile-toast-desc">{toast.msg}</span>
          </div>
          <button className="profile-toast-close" onClick={() => setToast(null)}>
            <SvgIcon name="x" size={16} />
          </button>
        </div>
      )}

      {/* Sidebar Navigation */}
      <Sidebar activeView="myRegistrations" onSwitchView={onSwitchView} onLogout={onLogout} user={user} />

      {/* Main Content Area */}
      <main className="profile-main-content">
        <header className="profile-top-bar my-reg-top-bar">
          <div className="profile-top-bar-left">
            <SvgIcon name="clipboard" size={22} color="#4f46e5" />
            <h1 className="admin-dashboard-title my-reg-header-title">My Course Registrations</h1>
          </div>
          <div className="profile-top-bar-right my-reg-header-actions">
            <button
              className="my-reg-btn my-reg-btn-attendance"
              onClick={() => {
                setAttendanceModalOpen(true);
                setAttendanceMsg(null);
                setSessionCodeInput('');
              }}
              title="Key in trainer attendance session code"
            >
              <SvgIcon name="checkSquare" size={16} />
              <span>Key In Attendance Code</span>
            </button>
            <button
              className="my-reg-btn my-reg-btn-refresh"
              onClick={() => fetchMyRegistrations(false)}
              title="Refresh registration status"
            >
              <SvgIcon name="refresh" size={15} />
              <span>Refresh Status</span>
            </button>
            <button
              className="my-reg-btn my-reg-btn-browse"
              onClick={() => onSwitchView('calendar')}
              title="Browse and register courses"
            >
              <SvgIcon name="calendar" size={16} />
              <span>Browse & Register Courses</span>
            </button>
          </div>
        </header>

        <div className="admin-dashboard-body my-reg-body">
          {/* Blackout Policy Suspension Alert */}
          {learnerBlackout?.isBlacklisted && (
            <div style={{
              background: '#fef2f2',
              border: '1px solid #f87171',
              borderLeft: '5px solid #dc2626',
              borderRadius: '8px',
              padding: '1rem 1.25rem',
              marginBottom: '1.25rem',
              display: 'flex',
              gap: '1rem',
              alignItems: 'flex-start'
            }}>
              <div style={{ color: '#dc2626', marginTop: '2px', flexShrink: 0 }}>
                <SvgIcon name="slash" size={24} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <h4 style={{ margin: 0, color: '#991b1b', fontSize: '1rem', fontWeight: 700 }}>
                    Account Enrollment Suspended (Blackout Active)
                  </h4>
                  <span style={{
                    background: '#dc2626',
                    color: '#fff',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '4px'
                  }}>
                    {learnerBlackout.daysRemaining} days remaining
                  </span>
                </div>
                <p style={{ margin: '6px 0 8px 0', fontSize: '0.85rem', color: '#7f1d1d', lineHeight: '1.45' }}>
                  Your course registration privileges have been suspended until <strong>{learnerBlackout.blackoutRecord?.EndDate ? new Date(learnerBlackout.blackoutRecord.EndDate).toLocaleDateString() : 'N/A'}</strong> due to repeated incomplete attendance / no-show registrations ({learnerBlackout.poorAttendanceCount || 3} infractions detected).
                </p>
                <div style={{ fontSize: '0.78rem', color: '#991b1b', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <span><strong>Reason:</strong> {learnerBlackout.blackoutRecord?.Reason || 'Repeated no-shows / poor attendance.'}</span>
                  <span>Need an exception? Please contact the course administration team.</span>
                </div>
              </div>
            </div>
          )}

          {/* Summary Status Filters */}
          <div className="my-reg-filter-bar">
            <button
              className={`reg-filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setActiveFilter('all')}
            >
              All Registrations ({registrations.length})
            </button>
            <button
              className={`reg-filter-btn ${activeFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setActiveFilter('pending')}
            >
              Pending Approval ({registrations.filter(r => r.Status === 'Pending Approval').length})
            </button>
            <button
              className={`reg-filter-btn ${activeFilter === 'payment' ? 'active' : ''}`}
              onClick={() => setActiveFilter('payment')}
            >
              Pending Payment / Slip ({registrations.filter(r => r.Status === 'Pending Payment' || r.Status === 'Payment Submitted').length})
            </button>
            <button
              className={`reg-filter-btn ${activeFilter === 'approved' ? 'active' : ''}`}
              onClick={() => setActiveFilter('approved')}
            >
              Confirmed & Paid ({registrations.filter(r => r.Status === 'Approved').length})
            </button>
          </div>

          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
              Loading your registrations...
            </div>
          ) : filteredRegistrations.length === 0 ? (
            <div className="no-reg-card">
              <SvgIcon name="clipboard" size={48} />
              <h3>No Registrations Found</h3>
              <p>You haven't registered for any training courses under this filter yet.</p>
              <button className="browse-courses-btn" onClick={() => onSwitchView('calendar')}>
                View Course Calendar
              </button>
            </div>
          ) : (
            <div className="reg-cards-grid">
              {filteredRegistrations.map(reg => {
                const isPendingApproval = reg.Status === 'Pending Approval';
                const isPendingPayment = reg.Status === 'Pending Payment';
                const isPaymentSubmitted = reg.Status === 'Payment Submitted';
                const isApproved = reg.Status === 'Approved';
                const isRejected = reg.Status === 'Rejected';
                const isWithdrawn = reg.Status === 'Withdrawn';
                const isCancelled = reg.Status === 'Cancelled';

                return (
                  <div key={reg.RegistrationID} className={`my-reg-card ${reg.Status.toLowerCase().replace(/\s+/g, '-')}`}>
                    {/* Card Header */}
                    <div className="my-reg-card-header">
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
                          <span className="cert-body-badge">{reg.CertificationBody || 'Cybersecurity'}</span>
                          {reg.IsReEnrollment === 1 && (
                            <span style={{
                              background: '#fef3c7',
                              color: '#92400e',
                              border: '1px solid #fde68a',
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <SvgIcon name="alertTriangle" size={12} color="#b45309" />
                              Re-Enrollment ({reg.PreviousAttendanceRate != null ? `${reg.PreviousAttendanceRate}% Prev.` : 'Poor Att.'})
                            </span>
                          )}
                        </div>
                        <h3 className="course-title">{reg.CourseName}</h3>
                        <span className="course-type-subtitle">{reg.CourseType || 'Training & Exam'}</span>
                      </div>
                      <div>
                        {isPendingApproval && <span className="status-pill status-warning">Waiting for Approval</span>}
                        {isPendingPayment && <span className="status-pill status-orange">Action Needed: Pay / Upload Slip</span>}
                        {isPaymentSubmitted && <span className="status-pill status-info">Bank Slip Uploaded</span>}
                        {isApproved && <span className="status-pill status-success">Confirmed & Paid</span>}
                        {isRejected && <span className="status-pill status-danger">Registration Rejected</span>}
                        {isWithdrawn && <span className="status-pill status-gray">Withdrawn</span>}
                        {isCancelled && <span className="status-pill status-danger" style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5' }}>Cancelled</span>}
                      </div>
                    </div>

                    {/* Card Body Details */}
                    <div className="my-reg-card-body">
                      {(() => {
                        const segs = parseSegments(reg.SessionSegments);
                        if (segs && segs.length > 1) {
                          return (
                            <div className="detail-item" style={{ alignItems: 'flex-start' }}>
                              <span className="detail-label">Schedule Dates:</span>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                {segs.map((s, idx) => (
                                  <span key={idx} className="detail-value" style={{ fontSize: '0.85rem' }}>
                                    <strong style={{ color: '#047857' }}>Part {idx + 1}:</strong> {formatToDMY(s.startDate)} - {formatToDMY(s.endDate)} <span style={{ color: '#64748b', fontSize: '0.78rem' }}>({s.startTime || reg.StartTime} - {s.endTime || reg.EndTime})</span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        return (
                          <>
                            <div className="detail-item">
                              <span className="detail-label">Schedule Dates:</span>
                              <span className="detail-value">{formatToDMY(reg.StartDate)} - {formatToDMY(reg.EndDate)}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Session Time:</span>
                              <span className="detail-value">{reg.StartTime} - {reg.EndTime}</span>
                            </div>
                          </>
                        );
                      })()}
                      <div className="detail-item">
                        <span className="detail-label">Trainer:</span>
                        <span className="detail-value">{reg.TrainerName || 'Assigned CCoE Trainer'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Course Fee:</span>
                        <span className="detail-value fee-highlight">{reg.Fee || 'RM 2,100'}</span>
                      </div>
                      {isApproved && (
                        <div className="detail-item" style={{ marginTop: '4px', paddingTop: '6px', borderTop: '1px dashed #cbd5e1' }}>
                          <span className="detail-label">Attendance:</span>
                          <span className="detail-value">
                            {reg.AttendanceStatus === 'Present' ? (
                              <span className="status-pill status-success" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                Present {reg.CheckInTime ? `(${new Date(reg.CheckInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})` : ''}
                              </span>
                            ) : (
                              <span className="status-pill status-warning" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                Not Checked In
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Banner status info box */}
                    {isPendingApproval && (
                      <div className="reg-info-banner warning-banner" style={reg.IsReEnrollment === 1 ? { borderLeft: '4px solid #f59e0b', background: '#fffbeb', color: '#92400e' } : {}}>
                        {reg.IsReEnrollment === 1 ? (
                          <div>
                            <strong>Re-Enrollment Special Review:</strong> You previously attended this course with {reg.PreviousAttendanceRate != null ? `${reg.PreviousAttendanceRate}%` : 'incomplete'} attendance. Your registration requires an administrator override before it can be confirmed.
                          </div>
                        ) : (
                          'Your registration request has been received and is waiting for Admin approval.'
                        )}
                      </div>
                    )}
                    {reg.OverrideReason && (
                      <div className="reg-info-banner" style={{ background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', borderLeft: '4px solid #10b981', borderRadius: '6px', padding: '0.65rem 0.85rem', fontSize: '0.8rem', lineHeight: '1.45', marginBottom: '0.5rem' }}>
                        <strong>Admin Approval Override:</strong> {reg.OverrideReason}
                      </div>
                    )}
                    {isPendingPayment && (
                      <div className="reg-info-banner orange-banner">
                        Admin has requested payment confirmation. You can pay directly via Stripe or upload your bank transfer slip below.
                      </div>
                    )}
                    {isPaymentSubmitted && (
                      <div className="reg-info-banner info-banner">
                        Your bank slip has been submitted! Admin is currently verifying your payment proof.
                      </div>
                    )}
                    {isApproved && (
                      <div className="reg-info-banner success-banner">
                        Enrollment Confirmed! A confirmation email with calendar invites and venue details has been sent to your inbox.
                      </div>
                    )}
                    {isCancelled && (
                      <div className="reg-info-banner" style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: '6px', padding: '0.65rem 0.85rem', fontSize: '0.8rem', lineHeight: '1.45' }}>
                        This course session was cancelled by the administrator. Reschedule details have been emailed to your registered inbox.
                      </div>
                    )}

                    {/* Card Footer Actions */}
                    <div className="my-reg-card-footer" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                      {(() => {
                        const matchedCerts = myCertificates.filter(c =>
                          String(c.ScheduleID) === String(reg.CourseScheduleID) ||
                          String(c.RegistrationID) === String(reg.RegistrationID)
                        );
                        if (matchedCerts.length === 0) return null;
                        return matchedCerts.map(cert => (
                          <a
                            key={cert.CertificateID}
                            href={`/api/certificates/download/${cert.CertificateNumber}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              padding: '0.45rem 0.85rem',
                              borderRadius: '6px',
                              background: 'linear-gradient(135deg, #065f46 0%, #047857 100%)',
                              color: '#ffffff',
                              fontWeight: '700',
                              fontSize: '0.78rem',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              boxShadow: '0 2px 4px rgba(4, 120, 87, 0.2)'
                            }}
                            title={`Download ${cert.TemplateType} Certificate of Completion PDF`}
                          >
                            <SvgIcon name="graduationCap" size={13} /> Download {cert.TemplateType === 'RCC' ? 'RCC Joint Cert' : 'CCoE Cert'} (PDF)
                          </a>
                        ));
                      })()}

                      {isApproved && reg.AttendanceStatus !== 'Present' && (
                        <button
                          className="action-btn-key-attendance"
                          onClick={() => {
                            setAttendanceModalOpen(true);
                            setAttendanceMsg(null);
                            setSessionCodeInput('');
                          }}
                        >
                          <SvgIcon name="checkSquare" size={14} />
                          <span>Key In Attendance Code</span>
                        </button>
                      )}

                      {(isPendingPayment || isPaymentSubmitted) && (
                        <a
                          href={reg.PaymentLink || 'https://buy.stripe.com/fZubJ0cfl5a91Vu3NPdjO1i'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="action-link-stripe"
                        >
                          Pay via Stripe Link
                        </a>
                      )}

                      {(isPendingPayment || isPaymentSubmitted) && (
                        <button
                          className="action-btn-upload"
                          onClick={() => {
                            setUploadTargetReg(reg);
                            setBankSlipFile(null);
                            setBankSlipPreview(null);
                          }}
                        >
                          {isPaymentSubmitted ? 'Re-upload Bank Slip' : 'Upload Bank Slip'}
                        </button>
                      )}

                      {reg.PaymentProofPath && (
                        <button
                          className="action-btn-view-proof"
                          onClick={() => setViewProofModal(reg.PaymentProofPath)}
                        >
                          View Bank Slip
                        </button>
                      )}

                      {(isPendingApproval || isPendingPayment) && (
                        <button
                          className="action-btn-withdraw-link"
                          onClick={() => setWithdrawingRegId(reg.RegistrationID)}
                        >
                          Withdraw
                        </button>
                      )}

                      {(isWithdrawn || isCancelled || isRejected) && (
                        <button
                          className="action-btn-upload"
                          style={{ background: '#4f46e5', color: '#ffffff' }}
                          onClick={() => onSwitchView('calendar')}
                        >
                          Register Again
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* ── UPLOAD BANK SLIP MODAL ── */}
      {uploadTargetReg && (
        <div className="admin-modal-overlay" onClick={() => setUploadTargetReg(null)}>
          <div className="admin-modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '620px', width: '90%', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', margin: 'auto' }}>
            <div className="admin-modal-header" style={{ background: '#4f46e5', color: '#ffffff', padding: '1.25rem 1.75rem', flexShrink: 0 }}>
              <div className="modal-header-title">
                <SvgIcon name="clipboard" size={20} />
                <h3 style={{ color: '#ffffff', margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Upload Payment Bank Slip</h3>
              </div>
              <button className="modal-close-btn" style={{ color: '#ffffff' }} onClick={() => setUploadTargetReg(null)}>&times;</button>
            </div>

            <form onSubmit={handleUploadSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', margin: 0 }}>
              <div style={{ padding: '1.5rem 1.75rem', overflowY: 'auto', flex: 1 }}>
                <div style={{ marginBottom: '1rem', background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '0.95rem', color: '#1e293b' }}>
                    {uploadTargetReg.CertificationBody} {uploadTargetReg.CourseName}
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>
                    Course Fee: <strong style={{ color: '#166534' }}>{uploadTargetReg.Fee || 'RM 2,100'}</strong>
                  </p>
                </div>

                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', fontSize: '0.825rem', fontWeight: 700, color: '#334155', marginBottom: '0.5rem' }}>
                    Select Bank Slip Image or Receipt (JPG, PNG, PDF - Max 5MB) *
                  </label>

                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handleFileChange}
                    style={{ display: 'block', width: '100%', padding: '0.6rem', border: '1px dashed #cbd5e1', borderRadius: '8px', background: '#ffffff', cursor: 'pointer' }}
                    required
                  />
                </div>

                {bankSlipPreview && (
                  <div style={{ marginBottom: '1rem', textAlign: 'center', background: '#f1f5f9', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '0.5rem' }}>Preview Uploaded Document:</span>
                    {bankSlipPreview.startsWith('data:image/') ? (
                      <div style={{ maxHeight: '240px', overflowY: 'auto', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#ffffff', padding: '6px' }}>
                        <img src={bankSlipPreview} alt="Bank Slip Preview" style={{ maxWidth: '100%', maxHeight: '360px', objectFit: 'contain' }} />
                      </div>
                    ) : (
                      <div style={{ padding: '1rem', background: '#ffffff', borderRadius: '6px', border: '1px solid #cbd5e1', color: '#1e293b', fontWeight: 600 }}>
                        📄 PDF Document Attached ({bankSlipFile?.name})
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Fixed Footer Buttons */}
              <div style={{ padding: '1rem 1.75rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', flexShrink: 0 }}>
                <button
                  type="button"
                  style={{ padding: '0.6rem 1.25rem', background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                  onClick={() => setUploadTargetReg(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || !bankSlipPreview}
                  style={{ padding: '0.6rem 1.5rem', background: '#4f46e5', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: uploading || !bankSlipPreview ? 'not-allowed' : 'pointer', boxShadow: '0 2px 4px rgba(79,70,229,0.2)' }}
                >
                  {uploading ? 'Uploading...' : 'Submit Bank Slip'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── VIEW BANK SLIP PREVIEW MODAL ── */}
      {viewProofModal && (
        <div className="admin-modal-overlay" onClick={() => setViewProofModal(null)}>
          <div className="admin-modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '680px', width: '90%', boxSizing: 'border-box', margin: 'auto' }}>
            <div className="admin-modal-header" style={{ background: '#1e293b', color: '#ffffff' }}>
              <div className="modal-header-title">
                <SvgIcon name="clipboard" size={20} />
                <h3 style={{ color: '#ffffff' }}>Uploaded Bank Slip / Payment Receipt</h3>
              </div>
              <button className="modal-close-btn" style={{ color: '#ffffff' }} onClick={() => setViewProofModal(null)}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem', textAlign: 'center', background: '#f8fafc', maxHeight: '75vh', overflowY: 'auto' }}>
              {viewProofModal && (viewProofModal.toLowerCase().includes('.pdf') || viewProofModal.startsWith('data:application/pdf')) ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
                  <iframe
                    src={viewProofModal}
                    title="Bank Slip Document"
                    style={{ width: '100%', height: '480px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#ffffff' }}
                  />
                  <a
                    href={viewProofModal}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.25rem', background: '#4f46e5', color: '#ffffff', borderRadius: '6px', textDecoration: 'none', fontWeight: 600, fontSize: '0.85rem' }}
                  >
                    <SvgIcon name="download" size={16} />
                    <span>Download / Open Bank Slip in New Tab</span>
                  </a>
                </div>
              ) : viewProofModal.startsWith('data:image/') || viewProofModal.startsWith('http') || viewProofModal.startsWith('/uploads') ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
                  <img src={viewProofModal} alt="Bank Slip Proof" style={{ maxWidth: '100%', maxHeight: '480px', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }} />
                  <a
                    href={viewProofModal}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', borderRadius: '6px', textDecoration: 'none', fontWeight: 600, fontSize: '0.85rem' }}
                  >
                    <SvgIcon name="search" size={15} />
                    <span>View Full Resolution Image</span>
                  </a>
                </div>
              ) : (
                <div style={{ padding: '2rem', background: '#ffffff', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                  <p style={{ margin: '0 0 1rem 0', color: '#334155', fontWeight: 600 }}>Uploaded Payment Proof File:</p>
                  <a href={viewProofModal} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.2rem', background: '#4f46e5', color: '#ffffff', borderRadius: '6px', textDecoration: 'none', fontWeight: 600 }}>
                    <SvgIcon name="download" size={16} />
                    <span>Download / Open Bank Slip Document</span>
                  </a>
                </div>
              )}
            </div>
            <div style={{ padding: '1rem 1.5rem', background: '#ffffff', borderTop: '1px solid #e2e8f0', textAlign: 'right' }}>
              <button
                style={{ padding: '0.5rem 1.2rem', background: '#64748b', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                onClick={() => setViewProofModal(null)}
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── WITHDRAW CONFIRMATION MODAL ── */}
      {withdrawingRegId && (
        <div className="admin-modal-overlay" onClick={() => setWithdrawingRegId(null)}>
          <div className="admin-modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px', width: '90%', boxSizing: 'border-box', margin: 'auto' }}>
            <div className="admin-modal-header" style={{ background: '#ef4444', color: '#ffffff' }}>
              <h3 style={{ color: '#ffffff', margin: 0 }}>Confirm Withdrawal</h3>
              <button className="modal-close-btn" style={{ color: '#ffffff' }} onClick={() => setWithdrawingRegId(null)}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <p style={{ margin: 0, color: '#334155', fontSize: '0.9rem', lineHeight: '1.5' }}>
                Are you sure you want to withdraw your registration for this training course session?
              </p>
            </div>
            <div style={{ padding: '1rem 1.5rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                style={{ padding: '0.55rem 1.1rem', background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                onClick={() => setWithdrawingRegId(null)}
              >
                Cancel
              </button>
              <button
                style={{ padding: '0.55rem 1.1rem', background: '#ef4444', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: 'pointer' }}
                onClick={() => handleExecuteWithdraw(withdrawingRegId)}
              >
                Confirm Withdraw
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── LEARNER SELF CHECK-IN MODAL ── */}
      {attendanceModalOpen && (
        <div className="admin-modal-overlay" onClick={() => setAttendanceModalOpen(false)}>
          <div className="admin-modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px', width: '90%', boxSizing: 'border-box', margin: 'auto' }}>
            <div className="admin-modal-header" style={{ background: '#10b981', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div className="modal-header-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <SvgIcon name="checkCircle" size={22} color="#ffffff" />
                <h3 style={{ color: '#ffffff', margin: 0, fontSize: '1.15rem' }}>Class Attendance Check-In</h3>
              </div>
              <button className="modal-close-btn" style={{ color: '#ffffff' }} onClick={() => setAttendanceModalOpen(false)}>&times;</button>
            </div>

            <form onSubmit={handleAttendanceSubmit}>
              <div style={{ padding: '1.5rem 1.5rem 0.5rem 1.5rem' }}>
                <p style={{ margin: '0 0 1.25rem 0', color: '#475569', fontSize: '0.875rem', lineHeight: '1.45' }}>
                  Please enter the <strong>6-character Attendance Code</strong> provided by your trainer during class to confirm your attendance.
                </p>

                {attendanceMsg && (
                  <div style={{
                    padding: '0.85rem 1rem',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    marginBottom: '1.25rem',
                    background: attendanceMsg.type === 'success' ? '#dcfce7' : '#fef2f2',
                    color: attendanceMsg.type === 'success' ? '#15803d' : '#b91c1c',
                    border: `1px solid ${attendanceMsg.type === 'success' ? '#bbf7d0' : '#fecaca'}`
                  }}>
                    {attendanceMsg.text}
                  </div>
                )}

                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem' }}>
                    Trainer Session Code:
                  </label>
                  <input
                    type="text"
                    value={sessionCodeInput}
                    onChange={(e) => setSessionCodeInput(e.target.value.toUpperCase())}
                    placeholder="e.g. 8F3A2B"
                    maxLength={10}
                    required
                    style={{
                      width: '100%',
                      padding: '0.8rem 1rem',
                      fontSize: '1.25rem',
                      fontWeight: '800',
                      textAlign: 'center',
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      border: '2px solid #cbd5e1',
                      borderRadius: '8px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{ padding: '1rem 1.5rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                <button
                  type="button"
                  style={{ padding: '0.55rem 1.1rem', background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                  onClick={() => setAttendanceModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingCode}
                  style={{ padding: '0.55rem 1.25rem', background: '#10b981', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: 'pointer' }}
                >
                  {submittingCode ? 'Verifying Code...' : 'Submit Check-In'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyRegistrations;
